/* eslint-disable @typescript-eslint/no-unused-vars */
import { useQuery, useSubscription } from '@apollo/client'
import { gql } from '~/__generated__'
import { GraphqlError } from '~/ui/errors'
import { useParams } from 'react-router-dom'
import { useForm, FormProvider } from 'react-hook-form'
import invariant from 'tiny-invariant'
import { Button } from '~/ui/button'
import {
  PlayCircle,
  Plus,
  Search,
  Pause,
  EllipsisVertical,
  X,
  GripVertical,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  Clock,
  Sparkles,
} from 'lucide-react'
import { useSafeMutation } from '~/common/use-safe-mutation'
import { useState, useRef, useEffect, useMemo } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '~/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '~/ui/dropdown-menu'
import { Input } from '~/ui/input'
import { ScrollArea } from '~/ui/scroll-area'
import { ReactSortable } from 'react-sortablejs'
import { toast } from '~/hooks/use-toast'
import { Badge } from '~/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '~/ui/select'
import { Tooltip, TooltipContent, TooltipTrigger } from '~/ui/tooltip'
import { SpotifySyncProgress } from '~/components/spotify-sync-progress'
import { GenerateAiTracksDialog } from './generate-ai-tracks-dialog'
import { Progress } from '~/ui/progress'

// GraphQL Queries and Mutations
export const playlistQuery = gql(`
  query Playlist($id: ID!) {
    playlist(id: $id) {
      id
      name
      playlistType
      isSpotifyPlaylist
      spotifyId
      aiGenerated
      aiPrompt
      playlistTracks {
        id
        position
        track {
          id
          name
          trackNumber
          durationMs
          audioUrl
          coverUrl
          playable
          isSpotifyTrack
          spotifyId
          licensingPro
          licensingProCheckedAt
          artists {
            id
            name
          }
          album {
            id
            name
          }
        }
      }
      aiGenerationStatus {
        status
        progress
        tracksGenerated
        totalTracks
        message
        error
      }
      createdAt
    }
  }
`)

const STATION_PREVIEW_TRACKS_QUERY = gql(`
  query StationPreviewTracks($playlistId: ID!) {
    stationPreviewTracks(playlistId: $playlistId) {
      id
      name
      coverUrl
      artists {
        id
        name
      }
      album {
        id
        name
      }
    }
  }
`)

const PLAYLIST_GENERATION_SUBSCRIPTION = gql(`
  subscription PlaylistScreenGenerationStatus($playlistId: ID!) {
    playlistGenerationStatus(playlistId: $playlistId) {
      playlistId
      status
      progress
      tracksGenerated
      totalTracks
      message
      completedAt
      error
      confidence
      businessContext
      extractedAttributes
    }
  }
`)

export const searchTracksQuery = gql(`
  query SearchTracks($query: String!, $licensingPro: LicensingProEnum) {
    tracks(query: $query, licensingPro: $licensingPro) {
      edges {
        node {
          id
          name
          durationMs
          audioUrl
          coverUrl
          playable
          isSpotifyTrack
          spotifyId
          licensingPro
          licensingProCheckedAt
          artists {
            id
            name
          }
          album {
            id
            name
          }
        }
      }
    }
  }
`)

export const addTrackToPlaylistMutation = gql(`
  mutation AddTrackToPlaylist($input: AddTrackToPlaylistInput!) {
    addTrackToPlaylist(input: $input) {
      playlistTrack {
        id
        position
        track {
          id
          name
          trackNumber
          durationMs
          audioUrl
          playable
          isSpotifyTrack
          spotifyId
          artists {
            id
            name
          }
          album {
            id
            name
          }
        }
      }
    }
  }
`)

export const removeTrackFromPlaylistMutation = gql(`
  mutation RemoveTrackFromPlaylist($input: RemoveTrackFromPlaylistInput!) {
    removeTrackFromPlaylist(input: $input) {
      success
    }
  }
`)

export const updateTrackPositionMutation = gql(`
  mutation UpdateTrackPosition($input: UpdateTrackPositionInput!) {
    updateTrackPosition(input: $input) {
      success
    }
  }
`)

export const syncSpotifyPlaylistMutation = gql(`
  mutation SyncSpotifyPlaylist($input: SyncSpotifyPlaylistInput!) {
    syncSpotifyPlaylist(input: $input) {
      playlist {
        id
        name
      }
      success
      error
      requiresReauth
      tracksAdded
      tracksUpdated
      syncHistoryId
    }
  }
`)

export const activeSyncHistoriesQuery = gql(`
  query ActiveSyncHistories($playlistId: ID!) {
    activeSyncHistories(playlistId: $playlistId) {
      id
      syncType
      status
      message
      startedAt
      resourceId
      spotifyResourceId
      isActive
      metadata
    }
  }
`)

const LICENSING_PRO_FILTER_OPTIONS = [
  { value: 'all', label: 'Any PRO' },
  { value: 'ASCAP', label: 'ASCAP' },
  { value: 'BMI', label: 'BMI' },
  { value: 'GMR', label: 'GMR' },
  { value: 'SESAC', label: 'SESAC' },
]

// Helper function to format duration
function formatDuration(ms: number) {
  const minutes = Math.floor(ms / 60000)
  const seconds = Math.floor((ms % 60000) / 1000)
  return `${minutes}:${seconds.toString().padStart(2, '0')}`
}

function formatLicensingTimestamp(timestamp?: string | null) {
  if (!timestamp) return 'Not checked'

  const parsed = new Date(timestamp)
  if (Number.isNaN(parsed.getTime())) {
    return 'Not checked'
  }

  return parsed.toLocaleString()
}

export const PlaylistScreen = () => {
  const { playlistId } = useParams()
  invariant(playlistId)

  // State for audio player
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTrackIndex, setCurrentTrackIndex] = useState(-1)
  const [currentTrackId, setCurrentTrackId] = useState<string | null>(null)
  const audioRef = useRef<HTMLAudioElement | null>(null)

  // State for track search dialog
  const [searchDialogOpen, setSearchDialogOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<any[]>([])
  const [previewTrackId, setPreviewTrackId] = useState<string | null>(null)
  const [searchLicensingPro, setSearchLicensingPro] = useState<string | undefined>('all')

  const [filterQuery, setFilterQuery] = useState('')
  const [isDragging, setIsDragging] = useState(false)
  const [draggedTrackId, setDraggedTrackId] = useState<string | null>(null)
  const [isSyncing, setIsSyncing] = useState(false)
  const [syncHistoryId, setSyncHistoryId] = useState<string | null>(null)
  const [generationStatus, setGenerationStatus] = useState<any>(null)
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false)
  const [isGeneratingTracks, setIsGeneratingTracks] = useState(false)

  // GraphQL mutations
  const [addTrackToPlaylist] = useSafeMutation(addTrackToPlaylistMutation)
  const [removeTrackFromPlaylist] = useSafeMutation(removeTrackFromPlaylistMutation)
  const [updateTrackPosition] = useSafeMutation(updateTrackPositionMutation)
  const [syncSpotifyPlaylist] = useSafeMutation(syncSpotifyPlaylistMutation)

  // Query for active sync histories
  const { data: activeSyncData } = useQuery(activeSyncHistoriesQuery, {
    variables: {
      playlistId,
    },
    skip: !playlistId,
    fetchPolicy: 'cache-and-network',
    pollInterval: 5000, // Poll every 5 seconds for active syncs
  })

  // Main playlist data query
  const { data, error, loading, refetch } = useQuery(playlistQuery, {
    variables: {
      id: playlistId,
    },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  })

  // Station preview tracks (only for stations)
  const playlistType = data?.playlist?.playlistType
  const isStation = playlistType === 'STATION'
  const { data: stationPreviewData, refetch: refetchStationPreview } = useQuery(
    STATION_PREVIEW_TRACKS_QUERY,
    {
    variables: { playlistId },
    skip: !isStation,
    fetchPolicy: 'cache-and-network',
    }
  )

  const isAiGenerated = Boolean(data?.playlist?.aiGenerated)

  useEffect(() => {
    const status = data?.playlist?.aiGenerationStatus?.status
    if (status === 'pending' || status === 'processing') {
      setIsGeneratingTracks(true)
    }
  }, [data?.playlist?.aiGenerationStatus?.status])

  useSubscription(PLAYLIST_GENERATION_SUBSCRIPTION, {
    variables: { playlistId: playlistId ?? '' },
    skip: !playlistId || (!isAiGenerated && !isGeneratingTracks),
    onData: ({ data: subscriptionData }) => {
      const nextStatus = subscriptionData?.data?.playlistGenerationStatus
      if (nextStatus) {
        setGenerationStatus(nextStatus)
      }
    },
  })

  // Search query
  const {
    data: searchData,
    loading: searchLoading,
    error: searchError,
    refetch: refetchSearch,
  } = useQuery(searchTracksQuery, {
    variables: {
      query: searchQuery,
      licensingPro: searchLicensingPro === 'all' ? null : (searchLicensingPro as any),
    },
    skip: !searchQuery || searchQuery.length < 2,
  })

  // Update search results when search data changes
  useEffect(() => {
    if (searchData?.tracks) {
      setSearchResults(searchData.tracks.edges.map((edge: any) => edge.node))
    }
  }, [searchData])

  // Extract and sort tracks from the playlist data
  const playlist = data?.playlist
  const playlistTrackCount = data?.playlist?.playlistTracks?.length ?? 0
  const stationPreviewTrackCount = stationPreviewData?.stationPreviewTracks?.length ?? 0
  const tracks =
    data?.playlist?.playlistTracks
      ?.map((playlistTrack: any) => ({
        id: playlistTrack.id,
        trackId: playlistTrack.track?.id,
        name: playlistTrack.track?.name,
        position: playlistTrack.position,
        trackNumber: playlistTrack.track?.trackNumber ?? undefined,
        durationMs: playlistTrack.track?.durationMs ?? undefined,
        audioUrl: playlistTrack.track?.audioUrl ?? undefined,
        artistNames: playlistTrack.track?.artists?.map((artist: any) => artist.name) ?? undefined,
        coverUrl: playlistTrack.track?.coverUrl ?? undefined,
        licensingPro: playlistTrack.track?.licensingPro ?? null,
        licensingProCheckedAt: playlistTrack.track?.licensingProCheckedAt ?? null,
      }))
      ?.sort((a: any, b: any) => a.position - b.position) ?? []

  useEffect(() => {
    if (!isAiGenerated && !isGeneratingTracks) return

    const status = generationStatus?.status
    if (status !== 'completed' && status !== 'failed') return

    if (status === 'completed' || status === 'failed') {
      setIsGeneratingTracks(false)
    }

    refetch()
    if (isStation) {
      refetchStationPreview()
    }
  }, [generationStatus?.status, isAiGenerated, isGeneratingTracks, isStation, refetch, refetchStationPreview])

  useEffect(() => {
    if (!isAiGenerated && !isGeneratingTracks) return

    const status = generationStatus?.status
    const generationActive = !status || status === 'pending' || status === 'processing'
    const hasNoTracks = isStation ? stationPreviewTrackCount === 0 : playlistTrackCount === 0

    if (!generationActive || !hasNoTracks) return

    const poll = window.setInterval(() => {
      refetch()
      if (isStation) {
        refetchStationPreview()
      }
    }, 3000)

    return () => window.clearInterval(poll)
  }, [
    generationStatus?.status,
    isAiGenerated,
    isGeneratingTracks,
    isStation,
    playlistTrackCount,
    stationPreviewTrackCount,
    refetch,
    refetchStationPreview,
  ])

  // Handle end of track playback
  useEffect(() => {
    const audioElement = audioRef.current
    if (audioElement) {
      const handleEnded = () => {
        if (currentTrackIndex < tracks.length - 1) {
          // Play next track
          playTrack(currentTrackIndex + 1)
        } else {
          // End of playlist
          setIsPlaying(false)
          setCurrentTrackIndex(-1)
          setCurrentTrackId(null)
        }
      }

      audioElement.addEventListener('ended', handleEnded)
      return () => {
        audioElement.removeEventListener('ended', handleEnded)
      }
    }
  }, [currentTrackIndex, tracks])

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilterQuery(e.target.value)
  }

  const clearFilter = () => {
    setFilterQuery('')
  }

  // Play a track by index
  const playTrack = (index: number) => {
    if (index >= 0 && index < filteredTracks.length) {
      const track = filteredTracks[index]
      if (track) {
        // Find the real index in the original tracks array
        const originalIndex = tracks.findIndex((t: any) => t.id === track.id)
        setCurrentTrackIndex(originalIndex)
        setCurrentTrackId(track.id)
        setIsPlaying(true)

        if (audioRef.current) {
          audioRef.current.src = track.audioUrl || ''
          audioRef.current.play().catch((err) => console.error('Error playing audio:', err))
        }
      }
    }
  }

  // Play or pause the current track
  const togglePlayPause = () => {
    if (!isPlaying && currentTrackIndex === -1 && tracks.length > 0) {
      // Start playing the first track
      playTrack(0)
    } else if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause()
      } else {
        audioRef.current.play().catch((err) => console.error('Error playing audio:', err))
      }
      setIsPlaying(!isPlaying)
    }
  }

  // Handle play button for entire playlist
  const handlePlayPlaylist = async () => {
    if (tracks.length > 0) {
      togglePlayPause()
    }
  }

  const canPlayPlaylist = useMemo(() => {
    return tracks.some((track: any) => track.audioUrl)
  }, [tracks])

  // Preview a track from search results
  const previewTrack = (track: any) => {
    if (previewTrackId === track.id) {
      // Toggle off if already playing
      setPreviewTrackId(null)
      if (audioRef.current) {
        audioRef.current.pause()
      }
    } else {
      setPreviewTrackId(track.id)
      if (audioRef.current) {
        audioRef.current.src = track.audioUrl
        audioRef.current.play().catch((err) => console.error('Error playing audio:', err))
      }
    }
  }

  const isTrackInPlaylist = (trackId: string) => {
    return tracks.some((track: any) => track.trackId === trackId)
  }

  // Sync Spotify playlist
  const handleSyncSpotifyPlaylist = async () => {
    if (!playlist?.isSpotifyPlaylist || !playlist?.spotifyId) {
      toast({
        title: 'Error',
        description: 'This is not a Spotify playlist',
        variant: 'destructive',
      })
      return
    }

    setIsSyncing(true)

    try {
      const result = await syncSpotifyPlaylist({
        variables: {
          input: {
            playlistId,
          },
        },
        refetchQueries: ['Playlist'],
      })

      if (result.data?.syncSpotifyPlaylist?.success) {
        // Set sync history ID to start progress tracking
        setSyncHistoryId(result.data.syncSpotifyPlaylist.syncHistoryId || null)
        console.log('Sync started successfully')
      } else {
        const error = result.data?.syncSpotifyPlaylist?.error
        toast({
          title: 'Sync failed',
          description: error || 'Failed to sync Spotify playlist',
          variant: 'destructive',
        })

        if (result.data?.syncSpotifyPlaylist?.requiresReauth) {
          // Handle reauth if needed
          console.log('Spotify reauth required')
        }
      }
    } catch (error) {
      console.error('Error syncing Spotify playlist:', error)
      toast({
        title: 'Error',
        description: 'Failed to sync Spotify playlist',
        variant: 'destructive',
      })
    } finally {
      // Keep isSyncing true until progress component signals completion
    }
  }

  // Add a track to the playlist
  const addTrack = async (track: any) => {
    try {
      const result = await addTrackToPlaylist({
        variables: {
          input: {
            playlistId,
            trackId: track.id,
          },
        },
        refetchQueries: ['Playlist'],
      })

      // Refetch to ensure we have the latest data
      await refetch()

      toast({
        title: 'Track added',
        description: `"${track.name}" added to playlist`,
      })
    } catch (error) {
      console.error('Error adding track:', error)
      toast({
        title: 'Error',
        description: 'Failed to add track to playlist',
        variant: 'destructive',
      })
    }
  }

  // Remove a track from the playlist
  const removeTrack = async (playlistTrackId: string) => {
    console.log('Removing track with ID:', playlistTrackId)
    try {
      await removeTrackFromPlaylist({
        variables: {
          input: {
            id: playlistTrackId,
          },
        },
        optimisticResponse: {
          removeTrackFromPlaylist: {
            success: true,
            __typename: 'RemoveTrackFromPlaylistPayload',
          },
        },
        refetchQueries: ['Playlist'],
      })

      // If the removed track was playing, stop playback
      if (
        currentTrackId &&
        tracks.find((t: any) => t.id === playlistTrackId)?.trackId === currentTrackId
      ) {
        setIsPlaying(false)
        setCurrentTrackIndex(-1)
        setCurrentTrackId(null)
        if (audioRef.current) {
          audioRef.current.pause()
        }
      }

      await refetch()

      toast({
        title: 'Track removed',
        description: 'Track removed from playlist',
      })
    } catch (error) {
      console.error('Error removing track:', error)
      toast({
        title: 'Error',
        description: 'Failed to remove track from playlist',
        variant: 'destructive',
      })
    }
  }

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value)
  }

  const searchForm = useForm({
    defaultValues: {
      searchQuery: '',
    },
  })

  const filterForm = useForm({
    defaultValues: {
      filterQuery: '',
    },
  })

  const filteredTracks = useMemo(() => {
    return filterQuery
      ? tracks.filter(
          (track: any) =>
            track.name?.toLowerCase().includes(filterQuery.toLowerCase()) ||
            track.artistNames?.some((name: string) =>
              name.toLowerCase().includes(filterQuery.toLowerCase())
            )
        )
      : tracks
  }, [filterQuery, tracks])

  // Use filteredTracks directly instead of maintaining separate sortedTracks state
  const sortedTracks = useMemo(() => {
    return isDragging ? filteredTracks : filteredTracks
  }, [filteredTracks, isDragging])

  const renderGenerationStatus = (currentTrackCount: number) => {
    if (!isAiGenerated && !isGeneratingTracks) return null

    const status = generationStatus?.status || (currentTrackCount > 0 ? 'completed' : 'pending')
    const progress = generationStatus?.progress ?? (currentTrackCount > 0 ? 1 : 0.05)
    const message =
      generationStatus?.message ||
      (currentTrackCount > 0
        ? 'Generation complete.'
        : 'Generating tracks in the background. This view updates automatically.')
    const error = generationStatus?.error
    const tracksGenerated = generationStatus?.tracksGenerated ?? currentTrackCount
    const totalTracks = generationStatus?.totalTracks ?? currentTrackCount

    return (
      <div className="mb-4 rounded-md border border-blue-200 bg-blue-50 p-4">
        <div className="mb-3 flex items-center justify-between">
          <div className="text-sm font-medium text-blue-900">
            {status === 'completed' ? 'Generation complete' : 'Generating with AI'}
          </div>
          {status === 'completed' && <CheckCircle className="h-4 w-4 text-green-600" />}
          {status === 'failed' && <AlertCircle className="h-4 w-4 text-red-600" />}
          {(status === 'processing' || status === 'pending') && (
            <Clock className="h-4 w-4 animate-spin text-blue-600" />
          )}
        </div>

        <Progress value={Math.max(0, Math.min(100, Math.round(progress * 100)))} />

        <div className="mt-2 flex items-center justify-between text-xs text-blue-700">
          <span>{message}</span>
          <span>{Math.round(progress * 100)}%</span>
        </div>

        {totalTracks > 0 && (
          <div className="mt-2 text-xs text-blue-700">
            Generated {tracksGenerated} of {totalTracks} tracks
          </div>
        )}

        {error && <div className="mt-2 rounded bg-red-100 p-2 text-xs text-red-700">{error}</div>}
      </div>
    )
  }

  // Handle sync completion
  const handleSyncComplete = () => {
    setIsSyncing(false)
    setSyncHistoryId(null)
    refetch() // Refresh playlist data
  }

  // Handle sync error
  const handleSyncError = () => {
    setIsSyncing(false)
    setSyncHistoryId(null)
  }

  // Check for active syncs when component loads or data changes
  useEffect(() => {
    const activeSyncs = activeSyncData?.activeSyncHistories
    if (activeSyncs && activeSyncs.length > 0 && !syncHistoryId) {
      // Find the most recent active sync
      const activeSync = activeSyncs[0]

      // Only set up progress tracking if the sync is truly active
      if (activeSync?.isActive && activeSync?.status === 'started') {
        setSyncHistoryId(activeSync.id)
        setIsSyncing(true)

        console.log(`Found active ${activeSync.syncType} operation for playlist:`, {
          syncId: activeSync.id,
          status: activeSync.status,
          startedAt: activeSync.startedAt,
          message: activeSync.message,
        })
      }
    }
  }, [activeSyncData, syncHistoryId])

  // Clear sync state when sync completes
  useEffect(() => {
    const activeSyncs = activeSyncData?.activeSyncHistories
    if (activeSyncs && activeSyncs.length === 0 && syncHistoryId) {
      // No active syncs found, clear the sync state
      setIsSyncing(false)
      setSyncHistoryId(null)
      console.log('All sync operations completed, clearing sync state')
    }
  }, [activeSyncData?.activeSyncHistories, syncHistoryId])

  // Error handling
  if (error) {
    return <GraphqlError error={error} />
  }

  if (!playlist && loading) {
    return <div className="p-6">Loading playlist...</div>
  }

  if (!playlist) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center p-6">
        <h1 className="text-2xl">Playlist not found</h1>
      </div>
    )
  }

  if (isStation) {
    const previewTracks = stationPreviewData?.stationPreviewTracks ?? []
    return (
      <div className="flex h-full flex-col">
        <div className="flex flex-shrink-0 items-center justify-between px-7 py-3">
          <div className="min-w-0">
            <h1 className="line-clamp-1 text-base font-medium">{playlist.name}</h1>
            <div className="text-muted-foreground mt-1 text-sm">
              Station • Prompt: {playlist.aiPrompt || '—'}
            </div>
          </div>
        </div>
        <hr className="flex-shrink-0 border-0 border-b" />
        <div className="flex-1 overflow-y-auto p-7">
          <h2 className="text-lg font-medium">Expected tracks</h2>
          <p className="text-muted-foreground mb-4 text-sm">
            These are preview tracks generated when the station was created.
          </p>
          {renderGenerationStatus(previewTracks.length)}
          {previewTracks.length === 0 ? (
            <div className="text-muted-foreground">Generating preview tracks...</div>
          ) : (
            <div className="space-y-2">
              {previewTracks.map((t: any) => (
                <div key={t.id} className="flex items-center gap-3 rounded-md border p-2">
                  {t.coverUrl ? (
                    <img src={t.coverUrl} className="h-10 w-10 rounded object-cover" />
                  ) : (
                    <div className="h-10 w-10 rounded bg-neutral-200" />
                  )}
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium">{t.name}</div>
                    <div className="text-muted-foreground truncate text-xs">
                      {t.artists?.map((a: any) => a.name).join(', ') || 'Unknown artist'}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col">
      {/* Hidden audio element for playback */}
      <audio ref={audioRef} />

      {/* Sync Progress Component */}
      {syncHistoryId && (
        <div className="mx-7 mt-4">
          <SpotifySyncProgress
            syncHistoryId={syncHistoryId}
            playlistId={playlistId}
            playlistName={playlist.name}
            syncType="sync"
            onComplete={handleSyncComplete}
            onError={handleSyncError}
            compact
          />
        </div>
      )}
      {(isAiGenerated || isGeneratingTracks) && (
        <div className="mx-7 mt-4">{renderGenerationStatus(tracks.length)}</div>
      )}
      {/* Playlist header */}
      <div className="flex flex-shrink-0 items-center justify-between px-7 py-3">
        <Tooltip>
          <TooltipTrigger asChild>
            <h1 className="mr-3 line-clamp-1 max-w-1/3 text-base font-medium">{playlist.name}</h1>
          </TooltipTrigger>
          <TooltipContent>
            <p>{playlist.name}</p>
          </TooltipContent>
        </Tooltip>
        <div className="flex w-1/3 items-center md:mx-4 lg:mx-2">
          <FormProvider {...filterForm}>
            <div className="flex h-9 w-full items-center justify-between rounded-md border px-3">
              <Search className="text-muted-foreground mr-2 h-4 w-4" />
              <Input
                className="!border-0 !bg-transparent px-0 focus:!border-0 focus:!bg-transparent focus-visible:!ring-0 focus-visible:!ring-offset-0 focus-visible:!outline-0"
                placeholder="Filter tracks in this playlist..."
                value={filterQuery}
                onChange={handleFilterChange}
              />
              {filterQuery && (
                <Button variant="ghost" size="sm" onClick={clearFilter} className="h-5 w-5 p-0">
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          </FormProvider>
          {filterQuery && (
            <div className="text-muted-foreground ml-2 text-sm">
              Showing {filteredTracks.length} of {tracks.length} tracks
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <Button
            className="cursor-pointer"
            variant="outline"
            size="sm"
            disabled={!canPlayPlaylist}
            onClick={handlePlayPlaylist}
          >
            {isPlaying ? (
              <>
                <Pause className="h-4 w-4 md:mr-2" />
                <span className="hidden text-sm md:block">Pause</span>
              </>
            ) : (
              <>
                <PlayCircle className="h-4 w-4 md:mr-2" />
                <span className="hidden text-sm md:block">Play</span>
              </>
            )}
          </Button>

          {playlist?.isSpotifyPlaylist && (
            <Button
              className="cursor-pointer"
              variant="outline"
              size="sm"
              onClick={handleSyncSpotifyPlaylist}
              disabled={isSyncing}
            >
              <RefreshCw className={`h-4 w-4 md:mr-2 ${isSyncing ? 'animate-spin' : ''}`} />
              <span className="hidden text-sm md:block">{isSyncing ? 'Syncing...' : 'Sync'}</span>
            </Button>
          )}

          <Button
            variant="outline"
            size="sm"
            className="cursor-pointer"
            onClick={() => setGenerateDialogOpen(true)}
            disabled={isGeneratingTracks}
          >
            <Sparkles className="h-4 w-4 md:mr-2" />
            <span className="hidden text-sm md:block">
              {isGeneratingTracks ? 'Generating...' : 'Generate AI Tracks'}
            </span>
          </Button>

          <GenerateAiTracksDialog
            playlistId={playlistId}
            open={generateDialogOpen}
            onOpenChange={setGenerateDialogOpen}
            onGenerated={() => setIsGeneratingTracks(true)}
          />

          <Dialog open={searchDialogOpen} onOpenChange={setSearchDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="default" size="sm" className="cursor-pointer">
                <Plus className="h-4 w-4 md:mr-2" />
                <span className="hidden text-sm md:block">Add Tracks</span>
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[600px]">
              <FormProvider {...searchForm}>
                <DialogHeader>
                  <DialogTitle>Add tracks to playlist</DialogTitle>
                </DialogHeader>

                <div className="mt-4 flex items-center rounded-md border px-3 py-2">
                  <Search className="text-muted-foreground mr-2 h-4 w-4" />
                  <Input
                    className="border-0 px-0 focus-visible:ring-0"
                    placeholder="Search for tracks..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                  />
                </div>

                <div className="mt-3">
                  <label className="mb-1 block text-sm font-medium">Filter by PRO</label>
                  <Select
                    value={searchLicensingPro}
                    onValueChange={(value) => setSearchLicensingPro(value)}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Any PRO" />
                    </SelectTrigger>
                    <SelectContent>
                      {LICENSING_PRO_FILTER_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <ScrollArea className="mt-4 h-[400px]">
                  {searchLoading ? (
                    <div className="flex justify-center p-4">Searching...</div>
                  ) : searchError ? (
                    <div className="p-4 text-red-500">Error searching tracks</div>
                  ) : searchResults.length === 0 ? (
                    <div className="text-muted-foreground p-4 text-center">
                      {searchQuery.length > 1 ? 'No tracks found' : 'Start typing to search'}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {searchResults.map((track) => (
                        <div
                          key={track.id}
                          className="flex items-center justify-between rounded-md border p-2"
                        >
                          <div className="flex flex-1 gap-3">
                            {track.coverUrl && (
                              <div className="flex h-11 w-11">
                                <img src={track.coverUrl} className="h-11 w-11 rounded" />
                              </div>
                            )}
                            <div className="flex flex-1 flex-col gap-0">
                              <div className="font-medium">{track.name}</div>
                              <div className="text-muted-foreground text-sm">
                                {track.artists.map((artist: any) => artist.name).join(', ')}
                              </div>
                              <div className="text-muted-foreground mt-1 flex flex-wrap items-center gap-2 text-xs">
                                <Badge variant={track.licensingPro ? 'outline' : 'secondary'}>
                                  {track.licensingPro ?? 'Unmatched'}
                                </Badge>
                                {track.licensingProCheckedAt && (
                                  <span className="text-[10px]">
                                    Verified {formatLicensingTimestamp(track.licensingProCheckedAt)}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="xs"
                              className="cursor-pointer"
                              onClick={() => previewTrack(track)}
                            >
                              {previewTrackId === track.id ? (
                                <Pause className="h-4 w-4" />
                              ) : (
                                <PlayCircle className="h-4 w-4" />
                              )}
                            </Button>
                            {isTrackInPlaylist(track.id) ? (
                              <Button
                                variant="outline"
                                size="xs"
                                className="text-destructive cursor-pointer"
                                onClick={() => {
                                  // Find the playlist track entry for this track
                                  const playlistTrack = tracks.find(
                                    (t: any) => t.trackId === track.id
                                  )
                                  if (playlistTrack) {
                                    removeTrack(playlistTrack.id)
                                  } else {
                                    console.error(
                                      'Could not find playlist track for track:',
                                      track.id
                                    )
                                    toast({
                                      title: 'Error',
                                      description: 'Failed to remove track from playlist',
                                      variant: 'destructive',
                                    })
                                  }
                                }}
                              >
                                Remove
                              </Button>
                            ) : (
                              <Button
                                variant="outline"
                                size="xs"
                                className="cursor-pointer"
                                onClick={() => addTrack(track)}
                              >
                                Add
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>

                <DialogFooter>
                  <Button variant="outline" onClick={() => setSearchDialogOpen(false)}>
                    Close
                  </Button>
                </DialogFooter>
              </FormProvider>
            </DialogContent>
          </Dialog>
        </div>
      </div>
      <hr className="flex-shrink-0 border-0 border-b" />

      <div className="min-h-0 flex-1">
        {tracks.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-12">
            <p className="text-muted-foreground mb-4">This playlist is empty</p>
            <Button variant="outline" onClick={() => setSearchDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Add Tracks
            </Button>
          </div>
        ) : (
          <div className="flex h-full flex-col">
            <div className="flex-1 overflow-y-auto">
              <table className="w-full table-fixed">
                <thead className="bg-background sticky top-0 z-10">
                  <tr className="border-b">
                    <th className="w-10"></th>
                    <th className="w-10"></th>
                    {/*<th className="w-12"></th>*/}
                    <th className="w-16 text-center">#</th>
                    <th className="w-[40%] px-4 py-2 text-left">Name</th>
                    <th className="w-[25%] px-4 py-2 text-left">Artist</th>
                    <th className="w-28 px-4 py-2 text-left">PRO</th>
                    <th className="w-24 text-center">Duration</th>
                    <th className="w-16"></th>
                  </tr>
                </thead>
                <ReactSortable
                  tag="tbody"
                  list={sortedTracks.map((track: any) => ({
                    id: track.id,
                    trackId: track.trackId,
                    name: track.name,
                    position: track.position,
                    trackNumber: track.trackNumber,
                    durationMs: track.durationMs,
                    audioUrl: track.audioUrl,
                    artistNames: track.artistNames,
                    coverUrl: track.coverUrl,
                    licensingPro: track.licensingPro,
                  }))}
                  setList={() => {}} // Prevent direct state updates
                  animation={150}
                  delay={0}
                  delayOnTouchOnly={true}
                  handle=".drag-handle"
                  ghostClass="opacity-50"
                  fallbackClass="shadow-lg"
                  forceFallback={true}
                  scroll={true}
                  bubbleScroll={true}
                  onStart={(evt) => {
                    setIsDragging(true)
                    if (evt.oldIndex !== undefined) {
                      setDraggedTrackId(sortedTracks[evt.oldIndex].id)
                    }
                  }}
                  onEnd={async (evt) => {
                    setIsDragging(false)
                    setDraggedTrackId(null)

                    if (
                      evt.oldIndex !== undefined &&
                      evt.newIndex !== undefined &&
                      evt.oldIndex !== evt.newIndex &&
                      draggedTrackId
                    ) {
                      try {
                        await updateTrackPosition({
                          variables: {
                            input: {
                              playlistId,
                              playlistTrackId: draggedTrackId,
                              newPosition: evt.newIndex + 1, // positions are 1-based in the API
                            },
                          },
                          refetchQueries: ['Playlist'],
                        })

                        toast({
                          title: 'Track moved',
                          description: 'Track position updated successfully',
                        })
                      } catch (error) {
                        console.error('Error reordering tracks:', error)
                        toast({
                          title: 'Error',
                          description: 'Failed to reorder tracks',
                          variant: 'destructive',
                        })
                      }
                    }
                  }}
                  group="tracks"
                  emptyInsertThreshold={5}
                >
                  {sortedTracks.map((track: any) => (
                    <tr
                      key={track.id}
                      className={`hover:bg-muted/50 border-b ${
                        currentTrackIndex === tracks.findIndex((t: any) => t.id === track.id)
                          ? 'bg-muted'
                          : ''
                      } ${!track.audioUrl ? 'text-muted-foreground' : ''}`}
                    >
                      <td className="drag-handle cursor-grab px-2 py-3">
                        <GripVertical className="text-muted-foreground mx-auto h-4 w-4" />
                      </td>

                      <td className="relative px-2 py-3 text-center">
                        {track.coverUrl && (
                          <>
                            <img
                              src={track.coverUrl}
                              className="absolute !z-1 h-8 w-8 rounded-lg object-cover"
                            />
                            <div className="absolute !z-2 h-8 w-8 rounded-lg bg-black/20" />
                          </>
                        )}
                        <Button
                          variant="default"
                          size="sm"
                          className="relative !z-5 flex h-8 w-8 cursor-pointer rounded-lg p-0"
                          disabled={!track.audioUrl}
                          onClick={() => {
                            const originalIndex = tracks.findIndex((t: any) => t.id === track.id)
                            if (currentTrackIndex === originalIndex && isPlaying) {
                              togglePlayPause()
                            } else {
                              const index = filteredTracks.findIndex((t: any) => t.id === track.id)
                              playTrack(index)
                            }
                          }}
                        >
                          {currentTrackIndex === tracks.findIndex((t: any) => t.id === track.id) &&
                          isPlaying ? (
                            <Pause className="z-10 h-5 w-5 text-white" />
                          ) : (
                            <PlayCircle className="z-10 h-5 w-5 text-white" />
                          )}
                        </Button>
                      </td>

                      <td className="px-2 py-3 text-center">
                        <Badge variant="outline">{track.position}</Badge>
                      </td>

                      <td className="overflow-hidden px-4 py-3 text-ellipsis whitespace-nowrap">
                        <div className="overflow-hidden text-sm text-ellipsis">{track.name}</div>
                      </td>

                      <td className="overflow-hidden px-4 py-3 text-ellipsis whitespace-nowrap">
                        <div className="overflow-hidden text-sm text-ellipsis">
                          {track.artistNames?.join(', ') || 'Unknown'}
                        </div>
                      </td>

                      <td className="px-4 py-3 text-sm">
                        <Badge variant={track.licensingPro ? 'outline' : 'secondary'}>
                          {track.licensingPro ?? 'Unmatched'}
                        </Badge>
                        {track.licensingProCheckedAt && (
                          <div className="text-[10px] text-neutral-500">
                            {formatLicensingTimestamp(track.licensingProCheckedAt)}
                          </div>
                        )}
                      </td>

                      <td className="px-2 py-3 text-center text-sm">
                        {track.durationMs ? formatDuration(track.durationMs) : '--:--'}
                      </td>

                      <td className="px-2 py-3 text-center">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="xs" className="cursor-pointer">
                              <EllipsisVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent className="w-56">
                            <DropdownMenuItem
                              className="text-destructive hover:text-destructive"
                              onClick={() => removeTrack(track.id)}
                            >
                              Remove Track
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </ReactSortable>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
